// اجزای مشترک صفحات ORG (AP-ORG-01): کلیدهای Query، شعب در دامنه کاربر، انتخاب شعبه و برچسب نام برچسب منبع.
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import { unwrap, type ResourceTagView, type RoomType } from '@phoenix/api-client';
import { useAccess, useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';

export const ROOM_TYPES: readonly RoomType[] = [
  'Consultation',
  'Procedure',
  'Laser',
  'Treatment',
  'Dental',
  'Imaging',
  'Counseling',
  'Recovery',
  'Other',
];

export const orgKeys = {
  departments: (tenantId: string | undefined, branchId: string | undefined) =>
    ['organization', 'departments', tenantId, branchId] as const,
  rooms: (tenantId: string | undefined) => ['organization', 'rooms', tenantId] as const,
  tags: (tenantId: string | undefined) => ['organization', 'resource-tags', tenantId] as const,
  members: (tenantId: string | undefined) => ['identity', 'memberships', 'active', tenantId] as const,
};

/** کد کوتاه حروف لاتین (هم‌ارز Department.IsValidCode / Room.IsValidCode سرور). */
export const ORG_CODE_PATTERN = /^[A-Za-z0-9][A-Za-z0-9_-]{0,31}$/;

/** کد برچسب (هم‌ارز ResourceTag.IsValidCode سرور). */
export const TAG_CODE_PATTERN = /^[A-Za-z][A-Za-z0-9_-]{0,31}$/;

export interface BranchOption {
  id: string;
  name: string;
}

/**
 * شعب در دامنه کاربر (branch_ids توکن — C-AGENT-241) با نام از «مستأجرهای من»؛ انتخاب پیش‌فرض شعبه فعال یا نخستین شعبه.
 */
export function useBranchSelection() {
  const { t } = useTranslation('org');
  const { activeTenant, session } = useTenantSession();
  const access = useAccess();
  const options = useMemo<BranchOption[]>(() => {
    const allowed = session?.branchIds ?? access.branchIds;
    return allowed.map((id) => ({
      id,
      name: activeTenant?.branches.find((branch) => branch.branchId === id)?.name || t('org:branch_unnamed'),
    }));
  }, [activeTenant, session, access.branchIds, t]);
  const preferred = session?.activeBranchId ?? options[0]?.id;
  const [branchId, setBranchId] = useState<string | undefined>(preferred);
  useEffect(() => {
    if (!branchId || !options.some((option) => option.id === branchId)) {
      setBranchId(preferred);
    }
  }, [branchId, options, preferred]);
  return { options, branchId, setBranchId, nameOf: (id: string) => options.find((o) => o.id === id)?.name };
}

/** انتخاب‌گر شعبه بالای صفحات (فقط شعب در دامنه). */
export function BranchFilter({
  options,
  value,
  onChange,
}: {
  options: readonly BranchOption[];
  value: string | undefined;
  onChange: (branchId: string) => void;
}) {
  const { t } = useTranslation('org');
  return (
    <TextField
      select
      size="small"
      label={t('org:branch_filter')}
      value={value ?? ''}
      onChange={(event) => onChange(event.target.value)}
      sx={{ minWidth: 220 }}
      slotProps={{ htmlInput: { 'data-testid': 'org-branch-filter' } }}
    >
      {options.map((option) => (
        <MenuItem key={option.id} value={option.id}>
          {option.name}
        </MenuItem>
      ))}
    </TextField>
  );
}

/** برچسب‌های منبع مستأجر (سیستمی و سفارشی). */
export function useResourceTags() {
  const { tenantApi, session } = useTenantSession();
  return useQuery({
    queryKey: orgKeys.tags(session?.tenantId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/organization/resource-tags')),
  });
}

/** نام نمایشی برچسب: سیستمی از i18n، سفارشی نام مستأجر. */
export function useTagLabel() {
  const { t } = useTranslation('org');
  return (tag: Pick<ResourceTagView, 'code' | 'name' | 'nameKey'> | undefined, code?: string) => {
    if (!tag) {
      return code ?? '';
    }
    return tag.nameKey ? t(tag.nameKey) : (tag.name ?? tag.code);
  };
}
